import { useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import { Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import {
    getClientScopeOfAppointmentStatus,
    getClientScopeOfAppointment,
    acceptScopeOfAppointment,
} from 'api/myClients';
import MuiButton from 'components/Common/Button/MuiButton';
import FormHOC from 'components/Common/Form/FormHOC';
import { PageLoadingCircle } from 'components/Common/Loading';
import { FEATURE_IDS } from 'constants/identifiers';
import { useConfigContext } from 'contexts/ConfigProvider';
import PageHeader from './Common/PageHeader';
import AuthSignatureForm from './ScopeOfAppointment/AuthSignatureForm';
import {
    FeedbackUnavailable,
    FeedbackCompleted,
    FeedbackCancelled,
} from './ScopeOfAppointment/FeedbackView';
import { useFormContext } from 'react-hook-form';

const STATUS = {
    PENDING: 1,
    ACCEPTED: 2,
    CANCELLED: 3,
};

const soaFormSchema = yup.object({
    scopeAuthSignature: yup.string().trim().required('This field is required'),
    scopeAuthSignatureDate: yup.date().required('This field is required'),
});

const ScopeOfAppointmentDetails = ({ details }) => {
    const formatDate = (value: string) => {
        return DateTime.fromISO(value).toFormat('MM/dd/yyyy');
    };

    const sections = [
        {
            title: 'Client information',
            items: [
                [
                    'Client name',
                    `${details?.memberFirstName} ${details?.memberLastName}`,
                ],
                ['Medicare ID', details?.memberHic],
                ['Date of birth', formatDate(details?.memberDateOfBirth)],
                ['Email', details?.memberEmail],
                ['Phone', details?.memberPhoneNumber],
            ],
        },
        {
            title: 'Authorized Representative (If Applicable)',
            items: [
                ['Name', details?.authRepName],
                ['Relationship to Beneficiary', details?.authRelationship],
            ],
        },
        {
            title: 'Contact Info',
            items: [
                ['Email', details?.memberEmail],
                ['Phone', details?.memberPhoneNumber],
            ],
        },
        {
            title: 'Plans to discuss',
            items: [
                ['Medicare Advantage HMO', details?.initialHmo],
                ['Medicare Advantage PPO', details?.initialPpo],
                ['Medicare Advantage POS', details?.initialPos],
                ['Medicare Advantage SNP', details?.initialSnp],
            ],
        },
        {
            title: 'Appointment details',
            items: [
                ['Agent name', details?.agentName],
                ['Agent phone', details?.agentPhone],
                ['Beneficiary name', details?.beneficiaryName],
                ['Beneficiary phone', details?.beneficiaryPhone],
                ['Beneficiary address', details?.beneficiaryAddress],
                ['Method of contact', details?.initialMethodContact],
                ['Represented plans', details?.representedPlans],
                ['Agent signature', details?.agentSignature],
                ['Appointment date', formatDate(details?.agentAppointmentDate)],
            ],
        },
    ];

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                mb: 3,
            }}
        >
            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    backgroundColor: 'grey.50',
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Scope of appointment details
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    Please review the information below before signing.
                </Typography>
            </Box>
            {sections.map((section) => (
                <Box
                    key={section.title}
                    sx={{
                        px: { xs: 2, sm: 2.5 },
                        py: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            mb: 1,
                            color: 'text.secondary',
                            fontWeight: 700,
                        }}
                    >
                        {section.title}
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, minmax(0, 1fr))',
                            },
                            gap: 1.25,
                        }}
                    >
                        {section.items.map(([label, value]) => (
                            <Box key={label}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block' }}
                                >
                                    {label}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600 }}
                                >
                                    {value}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            ))}

            <Box
                sx={{
                    px: { xs: 2, sm: 2.5 },
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Typography
                    variant="subtitle2"
                    sx={{
                        mb: 1,
                        color: 'text.secondary',
                        fontWeight: 700,
                    }}
                >
                    Additional Notes
                </Typography>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                    }}
                >
                    {details?.briefExplanation ?? 'No notes provided.'}
                </Typography>
            </Box>
        </Box>
    );
};

const ScopeOfAppointmentReviewForm = ({
    details,
    onComplete,
}: {
    details: any;
    onComplete: () => void;
}) => {
    const { handleSubmit } = useFormContext();

    const onSubmit = (data) => {
        const { scopeAuthSignature, scopeAuthSignatureDate } = data;
        acceptScopeOfAppointment(
            details.clientId,
            scopeAuthSignature,
            scopeAuthSignatureDate
        ).then(() => {
            onComplete();
        });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            <PageHeader />
            <Box sx={{ m: 5, mt: 13, width: '100%', maxWidth: '1000px' }}>
                <ScopeOfAppointmentDetails details={details} />
                <AuthSignatureForm />
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                    <MuiButton
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit(onSubmit)}
                    >
                        Submit Signature
                    </MuiButton>
                </Box>
            </Box>
        </Box>
    );
};

const ScopeOfAppointmentReview = () => {
    const { featureFlags } = useConfigContext();
    const { clientId } = useParams();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [scopeOfAppointment, setScopeOfAppointment] = useState<any>({});
    const [status, setStatus] = useState<number>();

    useEffect(() => {
        setIsLoading(true);
        getClientScopeOfAppointmentStatus(clientId ?? '')
            .then((status: number) => {
                setStatus(status ?? STATUS.CANCELLED);

                if (status === STATUS.PENDING) {
                    getClientScopeOfAppointment(clientId)
                        .then((data) => {
                            setScopeOfAppointment(data);
                        })
                        .catch(() => {
                            setScopeOfAppointment({});
                        });
                }
            })
            .catch(() => setStatus(STATUS.CANCELLED))
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    if (!featureFlags[FEATURE_IDS.STORE_SCOPE_OF_APPOINTMENTS]) {
        return null;
    }

    if (isLoading) {
        return <PageLoadingCircle />;
    }

    if (status === STATUS.CANCELLED) {
        return <FeedbackCancelled />;
    } else if (status === STATUS.ACCEPTED) {
        return <FeedbackCompleted />;
    } else if (status !== STATUS.PENDING) {
        return <FeedbackUnavailable />;
    }

    return (
        <FormHOC yupValidations={soaFormSchema}>
            <ScopeOfAppointmentReviewForm
                details={scopeOfAppointment}
                onComplete={() => setStatus(STATUS.ACCEPTED)}
            />
        </FormHOC>
    );
};

export default ScopeOfAppointmentReview;
